import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  FormControl,
  FormLabel,
  Stack,
  TextField,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { useCatalogSearch } from '@/data/catalog/search';
import { useInstitutionsSearch } from '@/data/institutions';
import type { Course } from '@/models/catalog/course';
import { mountCourseOptions, type CourseOption } from '@/utils/course-options';

type Institution = {
  name: string;
  id: number;
};

const formValidationSchema = z.object({
  course: z
    .object({
      id: z.number(),
      label: z.string(),
      offerId: z.number(),
    })
    .nullable(),
  courseType: z.string(),
  institution: z
    .object({
      name: z.string(),
      id: z.number(),
    })
    .nullable(),
});

type FormValues = z.infer<typeof formValidationSchema>;

const defaultValues: FormValues = {
  course: null,
  courseType: '',
  institution: null,
};

const handleErrormessage = (
  error: { message?: string } | undefined
): string | undefined => error?.message;
const DEBOUNCE_CATALOG_SEARCH_MS = 500;

export const CourseSelection = ({
  onSubmit,
}: {
  onSubmit: (course: Course) => void;
}) => {
  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(formValidationSchema),
  });
  const [selectedCourse, setSelectedCourse] = React.useState<Course | null>(
    null
  );
  const [catalogSearchTerm, setCatalogSearchTerm] = React.useState('');
  const { institutions, isFetching: institutionsLoading } =
    useInstitutionsSearch();

  const { course, institution } = watch();

  const catalogSearchFilters = {
    filters: {
      ...(institution && { institutions: [institution.id] }),
      ...(catalogSearchTerm && { terms: catalogSearchTerm }),
    },
  };

  const { catalog: catalogData, isFetching: fetchingCatalog } =
    useCatalogSearch(catalogSearchFilters);

  const debounceCatalogSearchTerm = React.useMemo(
    () =>
      debounce((value: string) => {
        setCatalogSearchTerm(value);
      }, DEBOUNCE_CATALOG_SEARCH_MS),
    []
  );

  const courseOptions = mountCourseOptions(catalogData || []);

  return (
    <form
      onSubmit={handleSubmit(() => {
        if (selectedCourse) {
          onSubmit(selectedCourse);
        }
      })}
      id="course-selection-form"
    >
      <Stack spacing={2}>
        <Controller
          control={control}
          name="institution"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} fullWidth>
              <Stack spacing={1}>
                <FormLabel htmlFor="institution">
                  Instituição de Ensino
                </FormLabel>
                <Autocomplete
                  {...field}
                  data-testid="institution-autocomplete"
                  getOptionKey={(option: Institution) => option.id}
                  getOptionLabel={(option: Institution) => option.name}
                  id="institution"
                  disabled={institutionsLoading}
                  isOptionEqualToValue={(a: Institution, b: Institution) =>
                    a.id === b.id
                  }
                  onChange={(_, option: Institution | null, reason: string) => {
                    if (reason === 'clear') {
                      reset({ ...defaultValues });
                    }
                    if (reason === 'selectOption') {
                      reset({ ...defaultValues, institution: option });
                    }
                  }}
                  openOnFocus
                  options={(institutions || []) as Institution[]}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={handleErrormessage(error)}
                      placeholder="Selecione"
                    />
                  )}
                  value={institution}
                />
              </Stack>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="course"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} fullWidth>
              <Stack spacing={1}>
                <FormLabel htmlFor="course">Curso</FormLabel>
                <Autocomplete
                  {...field}
                  autoSelect
                  clearOnBlur
                  data-testid="course-name-autocomplete"
                  disabled={!institution}
                  fullWidth
                  id="course"
                  includeInputInList
                  isOptionEqualToValue={(a: CourseOption, b: CourseOption) =>
                    a.id === b.id
                  }
                  loading={fetchingCatalog}
                  loadingText="Carregando..."
                  noOptionsText={
                    !catalogSearchTerm
                      ? 'Digite o nome de um curso'
                      : 'Não encontramos nenhum curso com esses parâmetros.'
                  }
                  onBlur={() => {
                    setCatalogSearchTerm('');
                  }}
                  onChange={(
                    _,
                    option: CourseOption | null,
                    reason: string
                  ) => {
                    const keepFields = {
                      ...defaultValues,
                      institution,
                    };
                    if (reason === 'clear') {
                      reset({ ...keepFields }, { keepDefaultValues: true });
                    }
                    if (reason === 'selectOption' && option) {
                      reset(
                        {
                          ...keepFields,
                          course: {
                            id: option.id,
                            label: option.label,
                            offerId: option.offerId,
                          },
                          courseType: option.courseType.type,
                        },
                        { keepDefaultValues: true }
                      );
                      setSelectedCourse(option.rawCourse);
                    }
                  }}
                  onInputChange={(_, value: string, reason: string) => {
                    if (reason === 'input') {
                      debounceCatalogSearchTerm(value);
                    }
                  }}
                  openOnFocus
                  options={courseOptions}
                  renderInput={(params) => (
                    <TextField
                      error={!!error}
                      helperText={handleErrormessage(error)}
                      placeholder="Selecione"
                      {...params}
                    />
                  )}
                  value={
                    course
                      ? (courseOptions.find((opt) => opt.id === course.id) ??
                        null)
                      : null
                  }
                />
              </Stack>
            </FormControl>
          )}
        />
      </Stack>
    </form>
  );
};
